import { useEffect, useId, useRef, useState, type CSSProperties, type JSX } from 'react'
import { api, NOTE_TAGS, type NoteSearchFilters, type NoteSearchHit, type NoteTag, type User } from '../api.js'
import { asWords, formatDate } from '../components/Pill.js'
import { T } from '../tokens.js'

/**
 * Notes across the whole book (F-17, R-29, spec §7.7: "search across the
 * book") — free text, tag, author and date range, all combinable, over
 * `GET /api/notes/search`.
 *
 * ## The filters combine; they do not replace one another
 *
 * Every control writes into one filter object and one effect turns that object
 * into one request, so "text AND tag" is the only thing this screen can express
 * — there is no code path where setting a second filter drops the first. The
 * alternative shape, a request per control, is the one that goes wrong
 * silently: the screen shows results for whichever control fired last while the
 * rail still displays both.
 *
 * ## What redaction means *here*
 *
 * A redacted note is **not a result**. That is the fourth of the five redaction
 * rules `packages/server/src/notes/search.ts` tabulates, and it is the strictest
 * one: the account feed serves a redacted row with its body withheld, because
 * there the row itself is the fact worth showing, but a search hit *is* the
 * body, and a bodiless hit would assert that the withheld text matched what the
 * rep typed. This screen therefore draws no "withheld" row and must never grow
 * one — the server does not send them and nothing here should imply they exist.
 *
 * ## The author dimension is "me", and that is a gap rather than a design
 *
 * R-29 names author as one of the four dimensions and the route takes an
 * `authorId`. But **no route serves a rep the list of users**:
 * `GET /api/users/assignable` is gated to a program manager, and a free-text
 * box taking a user id would put a storage identifier in front of a rep — the
 * thing `NoteComposer` refuses to do with tags. So the one author a rep can
 * name without a directory is themselves, and the control says exactly that.
 * Searching for a *colleague's* notes needs a route that does not exist yet;
 * that is a finding, not a decision this screen is entitled to make up.
 *
 * The control is absent altogether when no `user` is supplied, rather than
 * rendered inert: a checkbox that cannot know whose notes it would select is
 * the inert "keep me signed in" defect again.
 *
 * ## Empty is a designed state, and there are two of them
 *
 * "No notes match" and "no notes have been written" are different facts and the
 * screen can tell them apart honestly, because a search with no filters at all
 * is every non-redacted note in the book (`searchNotes(db, {})`). So zero
 * results with nothing set means the book has no notes; zero results with
 * something set means nothing matched. Neither sentence blames the reader and
 * neither reads as a failure — an unwritten book is the normal state of a
 * pilot, the same argument display rule 4 makes about an uncharacterized
 * account.
 *
 * ## Structure is in `data-*`, not in computed styles
 *
 * Every fact a test needs about a hit — which note, which account, which tag —
 * is an attribute. jsdom answers most computed style queries with `''`, and
 * three separate classes of test on this campaign went green against gutted
 * code by measuring the environment rather than the component.
 */

/** Zero results with at least one filter set. A statement about the search. */
export const NO_MATCH = 'No notes match this search. Widen a filter, or clear them to see every note in the book.'

/**
 * Zero results with nothing set at all — which is every non-redacted note in
 * the book, so this is a statement about the book and it is true.
 *
 * Not a prompt and not a reproach: nobody is behind on anything. Notes arrive
 * as reps write them, and the sentence says where they will show up.
 */
export const NO_NOTES = 'No notes have been written about any account yet. They appear here as reps record them.'

export const SEARCH_FAILED = 'Those notes could not be searched. Try again.'

export const SEARCHING = 'Searching the book…'

/** The heading, and the one place this screen names what it is. */
export const NOTE_SEARCH_HEADING = 'Notes across the book'

const microLabel: CSSProperties = {
  fontSize: T.micro.size,
  fontWeight: T.weight.semi,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  color: T.color.micro,
}

const field: CSSProperties = {
  border: `1px solid ${T.color.hairline}`,
  borderRadius: T.radius.square,
  padding: '7px 8px',
  fontFamily: T.fontFamily,
  fontSize: 13.5,
  color: T.color.ink,
  background: '#fff',
}

const rowPill: CSSProperties = {
  border: `1.5px solid ${T.color.rule}`,
  color: T.color.muted,
  background: '#fff',
  padding: '3px 10px',
  borderRadius: T.radius.pill,
  fontSize: 9.5,
  fontWeight: T.weight.bold,
  letterSpacing: '0.07em',
  textTransform: T.micro.transform,
}

type Load =
  | { phase: 'loading' }
  | { phase: 'failed' }
  | { phase: 'ready'; hits: NoteSearchHit[] }

export interface NoteSearchProps {
  /**
   * The signed-in rep, when the caller knows one.
   *
   * Optional, and absent means the "only my notes" control is not drawn at all
   * — see the docblock. Nothing else on this screen depends on it.
   */
  user?: User
}

export function NoteSearch({ user }: NoteSearchProps): JSX.Element {
  const [text, setText] = useState('')
  const [tag, setTag] = useState<NoteTag | ''>('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [mine, setMine] = useState(false)
  const [load, setLoad] = useState<Load>({ phase: 'loading' })

  // `useId` rather than literals: nothing stops this screen appearing twice on
  // a page later, and two controls sharing an id make the label point at
  // whichever the browser found first.
  const textId = useId()
  const tagId = useId()
  const fromId = useId()
  const toId = useId()

  const alive = useRef(true)
  /**
   * Which request the screen is waiting for. Typing issues one request per
   * keystroke, so several answers can be in flight at once and the slower one
   * is not the answer to the question on screen — the same guard `NoteFeed` and
   * `AccountDetail` put on their own refetches, for the same reason.
   */
  const era = useRef(0)

  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  const authorId = mine && user !== undefined ? user.id : ''

  useEffect(() => {
    const mineEra = era.current + 1
    era.current = mineEra
    setLoad({ phase: 'loading' })
    // One object, built from every control at once. `api.searchNotes` drops the
    // empty strings — `?text=` is a 400 on this route, deliberately — so
    // "nothing typed" and "no opinion" are the same thing on the wire without
    // this screen having to hold `undefined` in five pieces of state.
    const filters: NoteSearchFilters = { text, authorId, from, to }
    // Assigned rather than spelled `tag: undefined`: under
    // `exactOptionalPropertyTypes` an explicit `undefined` is not the same as an
    // absent key, and "any tag" is the absence of the filter.
    if (tag !== '') filters.tag = tag
    void api.searchNotes(filters)
      .then((hits) => {
        if (alive.current && era.current === mineEra) setLoad({ phase: 'ready', hits })
      })
      .catch(() => {
        if (alive.current && era.current === mineEra) setLoad({ phase: 'failed' })
      })
  }, [text, tag, authorId, from, to])

  /**
   * Whether the rep has narrowed anything at all. This is what separates the
   * two empty states, and it is computed from the controls rather than from the
   * request, so a filter the request dropped cannot make the screen claim the
   * book is empty.
   */
  const narrowed = text !== '' || tag !== '' || from !== '' || to !== '' || authorId !== ''

  return (
    <div data-testid="note-search" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div
        style={{
          background: '#fff',
          borderBottom: `1.5px solid ${T.color.rule}`,
          padding: '18px 24px',
          display: 'flex',
          alignItems: 'baseline',
          gap: 16,
        }}
      >
        <h1 style={{ fontSize: 23, fontWeight: T.weight.bold, letterSpacing: '-0.01em', margin: 0 }}>
          {NOTE_SEARCH_HEADING}
        </h1>
        <div aria-live="polite" data-testid="note-search-count" style={{ fontSize: 12, color: T.color.muted }}>
          {load.phase === 'ready'
            ? `${load.hits.length} ${load.hits.length === 1 ? 'note' : 'notes'}`
            : ''}
        </div>
      </div>

      {/*
        A `<form>` with no submit: the results follow the controls, so there is
        nothing to press. `onSubmit` is stopped anyway, because pressing Enter
        in a text input submits the nearest form and a page reload here would
        throw the search away.
      */}
      <form
        data-testid="note-search-filters"
        onSubmit={(event) => { event.preventDefault() }}
        style={{
          background: '#fff',
          borderBottom: `1.5px solid ${T.color.rule}`,
          padding: '14px 24px',
          display: 'flex',
          gap: 18,
          flexWrap: 'wrap',
          alignItems: 'flex-end',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label htmlFor={textId} style={microLabel}>Text</label>
          <input
            id={textId}
            type="search"
            value={text}
            onChange={(event) => { setText(event.target.value) }}
            style={{ ...field, minWidth: 240 }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label htmlFor={tagId} style={microLabel}>Tag</label>
          {/*
            `asWords` for the option text and the stored value underneath, the
            same split `NoteComposer` makes: no control a rep uses is labelled
            with a storage identifier, and a hand-written label table is one
            more thing to go stale when the configuration moves.
          */}
          <select
            id={tagId}
            value={tag}
            onChange={(event) => { setTag(event.target.value as NoteTag | '') }}
            style={field}
          >
            <option value="">Any tag</option>
            {NOTE_TAGS.map((value) => <option key={value} value={value}>{asWords(value)}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label htmlFor={fromId} style={microLabel}>From</label>
          <input
            id={fromId}
            type="date"
            value={from}
            onChange={(event) => { setFrom(event.target.value) }}
            style={field}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label htmlFor={toId} style={microLabel}>To</label>
          <input
            id={toId}
            type="date"
            value={to}
            onChange={(event) => { setTo(event.target.value) }}
            style={field}
          />
        </div>

        {user === undefined
          ? null
          : (
            <label
              data-testid="note-search-mine"
              style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: T.color.body }}
            >
              <input type="checkbox" checked={mine} onChange={(event) => { setMine(event.target.checked) }} />
              Only my notes
            </label>
          )}
      </form>

      <div data-testid="note-search-results" data-search-phase={load.phase} style={{ padding: '18px 24px' }}>
        {load.phase === 'loading'
          ? <p style={{ fontSize: 13.5, color: T.color.micro, margin: 0 }}>{SEARCHING}</p>
          : null}
        {load.phase === 'failed'
          ? (
            <p role="alert" style={{ fontSize: 13.5, color: T.color.body, margin: 0, maxWidth: '60ch' }}>
              {SEARCH_FAILED}
            </p>
          )
          : null}
        {load.phase === 'ready' && load.hits.length === 0
          ? (
            <p
              data-testid="note-search-empty"
              data-empty-reason={narrowed ? 'no-match' : 'no-notes'}
              style={{ fontSize: 13.5, color: T.color.micro, margin: 0, maxWidth: '60ch' }}
            >
              {narrowed ? NO_MATCH : NO_NOTES}
            </p>
          )
          : null}
        {load.phase === 'ready' ? load.hits.map((hit) => <Hit key={hit.id} hit={hit} />) : null}
      </div>
    </div>
  )
}

/**
 * One hit, with the account it belongs to as a real link.
 *
 * **A plain `<a href>`, not an intercepted click.** The targeting table
 * intercepts because a rep opens account after account out of it; a search
 * result is opened one at a time and leaving the search *is* what opening one
 * means, so the browser's own navigation is the whole behaviour needed — and it
 * is what the app bar's own section links already do. `http/static.ts` serves
 * the SPA shell for `/accounts/:id`, so the link lands on the drill-down and
 * Back returns here.
 */
function Hit({ hit }: { hit: NoteSearchHit }): JSX.Element {
  return (
    <article
      data-testid="note-hit"
      data-note-id={hit.id}
      data-account-id={hit.accountId}
      data-tag={hit.tag ?? ''}
      data-pinned={hit.pinned ? 'true' : 'false'}
      style={{ borderTop: `1px solid ${T.color.hairline}`, padding: '12px 0' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'baseline' }}>
        <div>
          <a
            href={`/accounts/${encodeURIComponent(hit.accountId)}`}
            style={{ fontSize: 14, fontWeight: T.weight.semi, color: T.color.ink }}
          >
            {hit.accountName}
          </a>
          <span style={{ fontSize: 12, color: T.color.micro, marginLeft: 8 }}>{hit.accountNumber}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
          {hit.tag === null ? null : <span style={rowPill}>{asWords(hit.tag)}</span>}
          {hit.pinned ? <span style={rowPill}>Pinned</span> : null}
        </div>
      </div>
      <p style={{ margin: '7px 0 0', fontSize: 13.5, lineHeight: 1.5, color: T.color.ink, maxWidth: '80ch' }}>
        {hit.body}
      </p>
      {/*
        R-27's half of the record: who wrote it and when. Both come off the
        server's row — this screen stamps nothing and derives nothing.
      */}
      <div style={{ marginTop: 5, fontSize: 12, color: T.color.micro }}>
        {`${hit.authorName} · ${formatDate(hit.createdAt)}`}
      </div>
    </article>
  )
}
